/**
 * Ultimadesk — Visibilité de la bulle de chat Shopify Inbox (<shopify-chat>)
 *
 * La bulle n'apparaît que là où elle ne gêne rien. Elle est masquée :
 * - tant qu'une fenêtre est ouverte (bannière de cookies, panier, menu, recherche, fenêtres) ;
 * - sur le premier écran de la page : elle arrive après un premier défilement (60 % de la
 *   hauteur de l'écran), sauf sur les pages d'aide (contact, FAQ…) où elle sert tout de suite ;
 * - sur une fiche produit, tant que la zone d'achat est à l'écran ;
 * - dès qu'elle recouvrirait une bannière, une vidéo, un bouton, un champ, des pastilles, les
 *   flèches d'un carrousel, la barre d'ajout collante ou le pied de page : on regarde ce qu'il
 *   y a sous elle.
 * Elle disparaît tout de suite et ne revient qu'après 400 ms sans gêne : pas de clignotement
 * pendant le défilement.
 */

const HIDDEN_CLASS = 'hide-chat-bubble';
const SHOW_DELAY = 400;
const HELP_PAGES = /^\/pages\/(contact|faq|aide|support|livraison|retour)/;

// Ce que la bulle ne doit jamais recouvrir
const PROTECTED = [
  '.image-banner-wrapper',
  '[class*="ud-hero"]',
  'video',
  'button',
  '.button',
  '[role="button"]',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[data-ud-tooltip]',
  'sticky-add-to-cart',
  '[data-ref="product-form"]',
  '[data-ref="footer"]',
  'footer',
].join(', ');

const root = document.documentElement;
let chat = null;
let clearSince = 0;
let showTimer = 0;
let frame = 0;

function isInViewport(element) {
  if (element == null) return false;
  const rect = element.getBoundingClientRect();
  return rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

function isOverlayOpen() {
  if (root.classList.contains('ultimadesk-cookie-banner-open')) return true;
  if (isInViewport(document.querySelector('#shopify-pc__banner'))) return true;
  // Menu, recherche, panier latéral : Fullstack bloque le défilement de la page pendant qu'ils sont ouverts
  if (document.body.classList.contains('overflow-hidden')) return true;
  return document.querySelector('cart-drawer.is-open, dialog:modal') != null;
}

function isFirstScreen() {
  if (HELP_PAGES.test(window.location.pathname)) return false;
  // Page trop courte pour défiler : la bulle reste disponible
  const scrollRoom = root.scrollHeight - window.innerHeight;
  return scrollRoom > window.innerHeight * 0.6 && window.scrollY < window.innerHeight * 0.6;
}

function isProductZoneInView() {
  return isInViewport(document.querySelector('[data-ref="product-form"]'));
}

// Emplacement du bouton de la bulle (repli : coin bas droit)
function bubbleRect() {
  const button = chat.shadowRoot?.querySelector('[part~="activator"]');
  const rect = button?.getBoundingClientRect();
  if (rect && rect.width > 0) return rect;
  return new DOMRect(window.innerWidth - 72, window.innerHeight - 72, 56, 56);
}

// Ce qu'il y a sous la bulle : son centre et ses quatre coins
function coversSomething() {
  const rect = bubbleRect();
  const points = [
    [0.5, 0.5],
    [0.15, 0.15],
    [0.85, 0.15],
    [0.15, 0.85],
    [0.85, 0.85],
  ];

  return points.some(([fx, fy]) => {
    const x = rect.left + rect.width * fx;
    const y = rect.top + rect.height * fy;
    if (x < 0 || y < 0 || x >= window.innerWidth || y >= window.innerHeight) return false;

    const element = document
      .elementsFromPoint(x, y)
      .find((candidate) => candidate !== chat && !chat.contains(candidate) && candidate !== root && candidate !== document.body);
    return element?.closest(PROTECTED) != null;
  });
}

function shouldHide() {
  return isOverlayOpen() || isFirstScreen() || isProductZoneInView() || coversSomething();
}

function update() {
  frame = 0;
  if (!chat?.isConnected) chat = document.querySelector('shopify-chat');
  if (chat == null) return;

  if (shouldHide()) {
    clearSince = 0;
    clearTimeout(showTimer);
    root.classList.add(HIDDEN_CLASS);
    return;
  }

  if (!root.classList.contains(HIDDEN_CLASS)) return;

  // Plus rien ne gêne : la bulle revient si c'est encore vrai dans 400 ms
  const now = performance.now();
  if (clearSince === 0) clearSince = now;
  if (now - clearSince >= SHOW_DELAY) {
    root.classList.remove(HIDDEN_CLASS);
    return;
  }

  clearTimeout(showTimer);
  showTimer = setTimeout(update, SHOW_DELAY - (now - clearSince) + 20);
}

function scheduleUpdate() {
  if (frame === 0) frame = requestAnimationFrame(update);
}

function initChatBubbleVisibility() {
  // Masquée au départ : la bulle de Shopify Inbox arrive après le chargement, sur le premier écran
  root.classList.add(HIDDEN_CLASS);
  update();

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate);

  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    new MutationObserver(update).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
  }

  // Fenêtres, bannière de cookies, bulle chargée plus tard, contenu qui bouge sans défilement
  setInterval(update, 700);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initChatBubbleVisibility, { once: true });
} else {
  initChatBubbleVisibility();
}
